import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig';

interface FormErrors {
  vehicleTypeName: string;
  selectedVehicleType: string;
}

interface VehicleType {
  id: number;
  type: string;
}

interface VehicleTypeData {
  status: string;
  data: VehicleType[];
  statusDescription?: string;
}

const AddVehicleType: React.FC = () => {
  const [vehicleTypeData, setVehicleTypeData] = useState<VehicleTypeData | null>(null);
  const [isVehicleTypeLoading, setIsVehicleTypeLoading] = useState<boolean>(true);
  const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState<string>("");
  const [newVehicleTypeName, setNewVehicleTypeName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    vehicleTypeName: "",
    selectedVehicleType: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle types on component mount
  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  const fetchVehicleTypes = () => {
    setIsVehicleTypeLoading(true);
    apiDefinitions.getAllVehicleTypes()
      .then((response: { data: VehicleTypeData }) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleTypeData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle types");
          setOpenFail(true);
        }
        setIsVehicleTypeLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "Failed to load vehicle types: Server error");
        setOpenFail(true);
        setIsVehicleTypeLoading(false);
      });
  };

  const validateVehicleTypeForm = (name: string): boolean => {
    const errors: FormErrors = { vehicleTypeName: "", selectedVehicleType: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleTypeName = "Vehicle type name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleType = (): boolean => {
    const errors: FormErrors = { vehicleTypeName: "", selectedVehicleType: "" };
    let isValid = true;

    if (!selectedVehicleTypeId) {
      errors.selectedVehicleType = "Please select a vehicle type";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleTypeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleTypeForm(newVehicleTypeName)) return;

    apiDefinitions.createVehicleType({ type: newVehicleTypeName })
      .then((response: { data: VehicleTypeData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle type added successfully");
          setNewVehicleTypeName("");
          fetchVehicleTypes();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle type with this name already exists.");
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle type creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle type with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "Failed to create vehicle type: Server error");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteVehicleType = () => {
    if (!validateSelectedVehicleType()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleType(parseInt(selectedVehicleTypeId))
        .then((response: { data: VehicleTypeData }) => {
          if (response.data.status === "S1000") {
            setSelectedVehicleTypeId("");
            fetchVehicleTypes();
          } else if (response.data.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("This vehicle type is already in use and cannot be deleted.");
          } else {
            showItemAlreadyUsed("This vehicle type is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("This vehicle type is already in use and cannot be deleted.");
          } else {
            showItemAlreadyUsed("This vehicle type is already in use and cannot be deleted.");
          }
        });
    });
  };

  const handleVehicleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleTypeId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleType: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Type</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Type</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleTypeName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleTypeName(e.target.value)}
                        placeholder="Enter vehicle type name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-type-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Type
                      </button>
                    </div>
                    {formErrors.vehicleTypeName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleTypeName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleTypeId}
                      onChange={handleVehicleTypeChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Type</option>
                      {isVehicleTypeLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleTypeData?.data?.map((vehicleType: VehicleType) => (
                          <option key={vehicleType.id} value={vehicleType.id}>{vehicleType.type}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleType && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleType}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleType}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Type Modal */}
      <div className="modal fade" id="vehicle-type-modal" tabIndex={-1} aria-labelledby="add-vehicle-type" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-type">Add New Vehicle Type</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleTypeSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleTypeName" className="form-label">Vehicle Type Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleTypeName"
                    value={newVehicleTypeName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleTypeName(e.target.value)}
                    placeholder="Enter vehicle type name (e.g., Car)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleTypeName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleTypeName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Type
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleType;